import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES, papelDeAcesso } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiPost, apiPut, apiPatch, apiDelete, fetchAllPaginated, fetchSchoolsSimple, API_BASE_URL } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import Pagination from '../components/Pagination';
import {
  STATUS_ALUNO,
  STATUS_VACANTE,
  alunoEstaNaTurma,
  classesStatusAluno,
  ehAlunoMatriculado,
  ehAlunoVacante,
  normalizarStatusAluno,
  rotuloStatusAluno,
} from '../utils/statusAluno';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import { baixarDocumentoAluno, podeImprimirFicha } from '../services/documentosAluno';
import {
  AbaFamilia, AbaSaude, CampoCidadeDaFicha, CamposDocumentosDaFicha,
  FICHA_VAZIA, fichaDoPerfil, fichaParaSalvar,
} from '../components/CamposFichaDoAluno';


// Imagem do selo TEA (fita de conscientização do autismo)
const TEA_BADGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='teaGrad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23FF6B6B'/%3E%3Cstop offset='25%25' style='stop-color:%23FFE66D'/%3E%3Cstop offset='50%25' style='stop-color:%234ECDC4'/%3E%3Cstop offset='75%25' style='stop-color:%2345B7D1'/%3E%3Cstop offset='100%25' style='stop-color:%23A855F7'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M32 4c-2 0-4 1-5 3l-8 16c-1 2-3 3-5 3H8c-2 0-4 2-4 4v4c0 2 2 4 4 4h6c2 0 4 1 5 3l8 16c1 2 3 3 5 3s4-1 5-3l8-16c1-2 3-3 5-3h6c2 0 4-2 4-4v-4c0-2-2-4-4-4h-6c-2 0-4-1-5-3L37 7c-1-2-3-3-5-3z' fill='url(%23teaGrad)' stroke='%23fff' stroke-width='2'/%3E%3Ccircle cx='32' cy='32' r='8' fill='%23fff'/%3E%3Cpath d='M28 30h8M32 26v8' stroke='url(%23teaGrad)' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E";

function Alunos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const userCity = useEffectiveCity();
  const cityId = userCity?.id || null;
  const isProfessor = user?.role === ROLES.PROFESSOR;
  // Diretor(a): matricula aluno novo e muda a situação (ativo, transferido...)
  // dos alunos da escola. Editar e excluir o cadastro fica com a Secretaria —
  // o backend recusa (DiretorMatriculaPermission).
  const isDiretor = user?.role === ROLES.DIRETOR;

  // Estados principais
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [filtroEscola, setFiltroEscola] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroTEA, setFiltroTEA] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Paginação (renderiza apenas a página atual — lista grande trava a tela)
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingAluno, setEditingAluno] = useState(null);
  const [modalTab, setModalTab] = useState('basico'); // Aba ativa do modal
  const [formData, setFormData] = useState({
    // Dados básicos
    student_name: '',
    student_serial: '',
    id_class: '',
    enrollment_date: '',
    status: 'enrolled',
    tea_grau: '',
    // Dados pessoais
    cpf: '',
    nis: '',
    nome_social: '',
    data_nascimento: '',
    nome_mae: '',
    genero: '',
    raca_cor: '',
    email: '',
    // Documentos
    rg: '',
    orgao_emissor_rg: '',
    cnh: '',
    // Endereço
    logradouro: '',
    bairro: '',
    numero_endereco: '',
    cep: '',
    co_municipio: '',
    co_uf: '',
    // Responsável
    responsavel_cpf: '',
    responsavel_nis: '',
    // Matrícula estendida
    etapa_ensino: '',
    ano_periodo: '',
    turno: '',
    co_matricula_rede: '',
    // Necessidades especiais
    deficiencia_baixa_visao: false,
    deficiencia_cegueira: false,
    deficiencia_auditiva: false,
    deficiencia_fisica: false,
    deficiencia_intelectual: false,
    deficiencia_surdez: false,
    deficiencia_surdocegueira: false,
    deficiencia_multipla: false,
    deficiencia_outras: false,
    deficiencia_outras_descricao: '',
    transtorno_espectro_autista: false,
    altas_habilidades_superdotacao: false,
    ppl: false,
    // Ficha de Matrícula: documentos, família e saúde
    ...FICHA_VAZIA,
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Opções de grau do AEE
  const teaGrauOptions = [
    { value: '', label: 'Não possui' },
    { value: 'nivel_1', label: 'Nível 1 - Leve' },
    { value: 'nivel_2', label: 'Nível 2 - Moderado' },
    { value: 'nivel_3', label: 'Nível 3 - Severo' },
  ];

  // Modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alunoToDelete, setAlunoToDelete] = useState(null);

  // Valores em inglês por compatibilidade com o backend — ver utils/statusAluno.
  const statusOptions = STATUS_ALUNO;

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Buscar alunos, turmas, escolas e perfis em paralelo
      const cityParams = cityId ? { city: cityId } : {};
      const [alunosData, turmasData, escolasData, profilesData] = await Promise.all([
        fetchAllPaginated('/students/', cityParams),
        fetchAllPaginated('/classes/', cityParams),
        fetchSchoolsSimple(cityId),
        fetchAllPaginated('/student-profiles/', cityParams),
      ]);

      // Criar mapa de perfis por id_student
      const profilesMap = {};
      profilesData.forEach(p => {
        profilesMap[p.id_student] = p;
      });

      // O professor só vê quem ainda está nas turmas dele. O vacante (e o
      // transferido, o desistente...) continua preso à última turma para a
      // secretaria reencontrá-lo, e por isso a API ainda o devolve — o
      // diário precisa dele para mostrar o histórico. Mas ele já saiu da
      // caderneta e não é mais aluno deste professor.
      const alunosVisiveis = isProfessor
        ? alunosData.filter(alunoEstaNaTurma)
        : alunosData;

      // Adicionar perfil aos alunos
      const alunosComPerfil = alunosVisiveis.map(aluno => ({
        ...aluno,
        profile: profilesMap[aluno.id_student] || null,
        has_tea: profilesMap[aluno.id_student]?.has_tea || false,
        tea_grau: profilesMap[aluno.id_student]?.tea_grau || '',
        turno: profilesMap[aluno.id_student]?.turno || '',
      }));

      alunosComPerfil.sort((a, b) => (a.student_name || '').localeCompare(b.student_name || '', 'pt-BR'));
      setAlunos(alunosComPerfil);
      setTurmas(turmasData);
      setEscolas(escolasData);

    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [cityId, isProfessor]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Obter turmas filtradas por escola
  const turmasFiltradas = filtroEscola
    ? turmas.filter(t => t.id_school === parseInt(filtroEscola))
    : turmas;

  // Filtrar alunos
  const alunosFiltrados = alunos.filter(aluno => {
    const turmaAluno = turmas.find(t => t.id === aluno.id_class);

    const matchEscola = !filtroEscola || (turmaAluno && turmaAluno.id_school === parseInt(filtroEscola));
    const matchTurma = !filtroTurma || aluno.id_class === parseInt(filtroTurma);
    const matchStatus = !filtroStatus || normalizarStatusAluno(aluno.status) === filtroStatus;
    const matchTEA = filtroTEA === '' ||
      (filtroTEA === 'true' && (aluno.has_tea || aluno.tea_grau)) ||
      (filtroTEA === 'false' && !aluno.has_tea && !aluno.tea_grau) ||
      (filtroTEA === 'nivel_1' && aluno.tea_grau === 'nivel_1') ||
      (filtroTEA === 'nivel_2' && aluno.tea_grau === 'nivel_2') ||
      (filtroTEA === 'nivel_3' && aluno.tea_grau === 'nivel_3');
    const matchSearch = !searchTerm ||
      aluno.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      aluno.student_serial?.toString().includes(searchTerm);

    return matchEscola && matchTurma && matchStatus && matchTEA && matchSearch;
  });

  // Volta para a primeira página sempre que a filtragem muda de tamanho
  // (evita ficar numa página que deixou de existir).
  useEffect(() => {
    setPagina(1);
  }, [filtroEscola, filtroTurma, filtroStatus, filtroTEA, searchTerm, porPagina]);

  // Apenas a fatia da página atual vai para o DOM
  const alunosPaginados = alunosFiltrados.slice(
    (pagina - 1) * porPagina,
    pagina * porPagina
  );

  // Contagem de alunos com TEA
  const alunosTEA = alunos.filter(a => a.has_tea || a.tea_grau).length;

  // Verificar permissões
  // Gestor / Referência Técnica / Professor: apenas leitura.
  // Supervisor pode editar mas não criar (regra do backend RoleBasedPermission).
  // Diretor(a) entra aqui pelo "+ Novo Aluno"; nas linhas ele só muda a situação.
  const canEdit = user?.role === ROLES.ADMIN
    || user?.role === ROLES.COORDENADOR
    || papelDeAcesso(user?.role) === ROLES.SUPERVISOR
    || user?.role === ROLES.REFERENCIA_TECNICA;

  // Mudar situação (Diretor)
  const [alunoSituacao, setAlunoSituacao] = useState(null);
  const [novaSituacao, setNovaSituacao] = useState('');
  const [salvandoSituacao, setSalvandoSituacao] = useState(false);
  const [erroSituacao, setErroSituacao] = useState(null);

  const abrirSituacao = (aluno) => {
    setAlunoSituacao(aluno);
    setNovaSituacao(normalizarStatusAluno(aluno.status));
    setErroSituacao(null);
  };

  const salvarSituacao = async () => {
    setSalvandoSituacao(true);
    setErroSituacao(null);
    try {
      await apiPatch(`/students/${alunoSituacao.id_student}/`, { status: novaSituacao });
      setAlunoSituacao(null);
      fetchData();
    } catch (err) {
      setErroSituacao(err.message || 'Não foi possível mudar a situação.');
    } finally {
      setSalvandoSituacao(false);
    }
  };

  // Estado para controlar download da declaração
  const [downloadingDeclaracao, setDownloadingDeclaracao] = useState(null);

  // Ficha de matrícula oferecida logo depois de matricular
  const [fichaDoNovoAluno, setFichaDoNovoAluno] = useState(null);
  const [gerandoFicha, setGerandoFicha] = useState(false);
  const [erroFicha, setErroFicha] = useState(null);

  // Baixa a Declaração de Frequência (DOCX) do aluno
  const handleDownloadDeclaracao = async (aluno) => {
    if (!aluno?.id_student) {
      setError('Aluno inválido para gerar declaração.');
      return;
    }
    setDownloadingDeclaracao(aluno.id_student);
    try {
      const response = await fetch(
        `${API_BASE_URL}/students/${aluno.id_student}/declaracao-frequencia/`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        let msg = `Erro ao gerar declaração (HTTP ${response.status}).`;
        try {
          const data = await response.json();
          if (data?.error) msg = data.error;
        } catch (_) { /* resposta sem JSON */ }
        throw new Error(msg);
      }

      const blob = await response.blob();

      // Extrai nome do arquivo do header
      const disposition = response.headers.get('Content-Disposition');
      let filename = `declaracao_frequencia_${aluno.student_name || 'aluno'}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Erro ao baixar declaração:', err);
      setError(err.message || 'Erro ao baixar declaração de frequência.');
    } finally {
      setDownloadingDeclaracao(null);
    }
  };

  // Abrir perfil do aluno (navega para página separada)
  const handleViewProfile = (aluno) => {
    if (!aluno?.id_student) {
      console.error('Erro: aluno sem id_student', aluno);
      setError('Não foi possível abrir o perfil do aluno. ID inválido.');
      return;
    }
    navigate(`/alunos/${aluno.id_student}`);
  };

  // Abrir modal para criar novo aluno
  const handleCreate = () => {
    setEditingAluno(null);
    setModalTab('basico');
    setFormData({
      // Dados básicos
      student_name: '',
      student_serial: '',
      id_class: turmas.length === 1 ? turmas[0].id : '',
      enrollment_date: new Date().toISOString().split('T')[0],
      status: 'enrolled',
      tea_grau: '',
      // Dados pessoais
      cpf: '',
      nis: '',
      nome_social: '',
      data_nascimento: '',
      nome_mae: '',
      genero: '',
      raca_cor: '',
      email: '',
      // Documentos
      rg: '',
      orgao_emissor_rg: '',
      cnh: '',
      // Endereço
      logradouro: '',
      bairro: '',
      numero_endereco: '',
      cep: '',
      co_municipio: '',
      co_uf: '',
      // Responsável
      responsavel_cpf: '',
      responsavel_nis: '',
      // Matrícula estendida
      etapa_ensino: '',
      ano_periodo: '',
      turno: '',
      co_matricula_rede: '',
      // Necessidades especiais
      deficiencia_baixa_visao: false,
      deficiencia_cegueira: false,
      deficiencia_auditiva: false,
      deficiencia_fisica: false,
      deficiencia_intelectual: false,
      deficiencia_surdez: false,
      deficiencia_surdocegueira: false,
      deficiencia_multipla: false,
      deficiencia_outras: false,
      deficiencia_outras_descricao: '',
      transtorno_espectro_autista: false,
      altas_habilidades_superdotacao: false,
      ppl: false,
      // Ficha de Matrícula: documentos, família e saúde
      ...FICHA_VAZIA,
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Abrir modal para editar aluno
  const handleEdit = (aluno) => {
    setEditingAluno(aluno);
    setModalTab('basico');
    const profile = aluno.profile || {};
    // Reconstrói os checkboxes de deficiência a partir do texto salvo (tipo_deficiencia)
    const tiposDef = (profile.tipo_deficiencia || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const KNOWN_DEF = ['Baixa Visão', 'Cegueira', 'Deficiência Auditiva', 'Deficiência Física', 'Deficiência Intelectual', 'Surdez', 'Surdocegueira', 'Deficiência Múltipla'];
    const hasDef = (label) => tiposDef.some((t) => t.toLowerCase() === label.toLowerCase());
    const outrasDef = tiposDef.filter((t) => !KNOWN_DEF.some((k) => k.toLowerCase() === t.toLowerCase()));
    setFormData({
      // Dados básicos
      student_name: aluno.student_name || '',
      student_serial: aluno.student_serial || '',
      id_class: aluno.id_class || '',
      enrollment_date: aluno.enrollment_date || '',
      status: aluno.status || 'enrolled',
      tea_grau: profile.tea_grau || '',
      // Dados pessoais
      cpf: profile.cpf || '',
      nis: profile.nis || '',
      nome_social: profile.nome_social || '',
      data_nascimento: profile.data_nascimento || '',
      nome_mae: profile.nome_mae || '',
      genero: profile.genero || '',
      raca_cor: profile.raca_cor || '',
      email: profile.email || '',
      // Documentos
      rg: profile.rg || '',
      orgao_emissor_rg: profile.orgao_emissor_rg || '',
      cnh: '',
      // Endereço
      logradouro: profile.logradouro || '',
      bairro: profile.bairro || '',
      numero_endereco: profile.numero_endereco || '',
      cep: profile.cep || '',
      co_municipio: profile.co_municipio || '',
      co_uf: profile.co_uf || '',
      // Responsável
      responsavel_cpf: profile.responsavel_cpf || '',
      responsavel_nis: profile.responsavel_nis || '',
      // Matrícula estendida
      etapa_ensino: profile.etapa_ensino || '',
      ano_periodo: profile.ano_periodo || '',
      turno: profile.turno || '',
      co_matricula_rede: '',
      // Necessidades especiais
      deficiencia_baixa_visao: hasDef('Baixa Visão'),
      deficiencia_cegueira: hasDef('Cegueira'),
      deficiencia_auditiva: hasDef('Deficiência Auditiva'),
      deficiencia_fisica: hasDef('Deficiência Física'),
      deficiencia_intelectual: hasDef('Deficiência Intelectual'),
      deficiencia_surdez: hasDef('Surdez'),
      deficiencia_surdocegueira: hasDef('Surdocegueira'),
      deficiencia_multipla: hasDef('Deficiência Múltipla'),
      deficiencia_outras: outrasDef.length > 0,
      deficiencia_outras_descricao: outrasDef.join(', '),
      transtorno_espectro_autista: profile.has_tea || false,
      altas_habilidades_superdotacao: profile.altas_habilidades_superdotacao || false,
      ppl: profile.ppl || false,
      // Ficha de Matrícula: documentos, família e saúde
      ...fichaDoPerfil(profile),
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};
    if (!formData.student_name.trim()) errors.student_name = 'Nome do aluno é obrigatório';
    if (!formData.student_serial.toString().trim()) errors.student_serial = 'Matrícula é obrigatória';
    if (!formData.id_class) errors.id_class = 'Turma é obrigatória';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar aluno
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const { tea_grau } = formData;

      // Enviar apenas os campos aceitos pelo endpoint /students/
      const dataToSend = {
        student_name: formData.student_name,
        student_serial: formData.student_serial,
        id_class: parseInt(formData.id_class),
        enrollment_date: formData.enrollment_date || null,
        status: formData.status || 'enrolled',
      };

      let studentId;
      if (editingAluno) {
        await apiPut(`/students/${editingAluno.id_student}/`, dataToSend);
        studentId = editingAluno.id_student;
      } else {
        const newStudent = await apiPost('/students/', dataToSend);
        studentId = newStudent.id_student;
      }

      // Atualizar ou criar perfil do aluno com TEA e deficiências
      if (studentId) {
        // Montar tipo_deficiencia a partir dos checkboxes
        const deficiencias = [];
        if (formData.deficiencia_baixa_visao) deficiencias.push('Baixa Visão');
        if (formData.deficiencia_cegueira) deficiencias.push('Cegueira');
        if (formData.deficiencia_auditiva) deficiencias.push('Deficiência Auditiva');
        if (formData.deficiencia_fisica) deficiencias.push('Deficiência Física');
        if (formData.deficiencia_intelectual) deficiencias.push('Deficiência Intelectual');
        if (formData.deficiencia_surdez) deficiencias.push('Surdez');
        if (formData.deficiencia_surdocegueira) deficiencias.push('Surdocegueira');
        if (formData.deficiencia_multipla) deficiencias.push('Deficiência Múltipla');
        if (formData.deficiencia_outras && formData.deficiencia_outras_descricao) {
          deficiencias.push(formData.deficiencia_outras_descricao);
        } else if (formData.deficiencia_outras) {
          deficiencias.push('Outras');
        }

        const profileData = {
          id_student: studentId,
          tea_grau: tea_grau || '',
          has_tea: tea_grau ? true : false,
          tem_deficiencia: deficiencias.length > 0 || formData.transtorno_espectro_autista,
          tipo_deficiencia: deficiencias.join(', ') || null,
          necessita_aee: tea_grau ? true : false,
          data_nascimento: formData.data_nascimento || null,
          genero: formData.genero || null,
          raca_cor: formData.raca_cor || null,
          turno: formData.turno || null,
          // Documentos / identificação
          cpf: formData.cpf || null,
          nis: formData.nis || null,
          nome_social: formData.nome_social || null,
          nome_mae: formData.nome_mae || null,
          email: formData.email || null,
          rg: formData.rg || null,
          orgao_emissor_rg: formData.orgao_emissor_rg || null,
          // Endereço
          logradouro: formData.logradouro || null,
          numero_endereco: formData.numero_endereco || null,
          bairro: formData.bairro || null,
          cep: formData.cep || null,
          co_municipio: formData.co_municipio || null,
          co_uf: formData.co_uf || null,
          // Responsável
          responsavel_cpf: formData.responsavel_cpf || null,
          responsavel_nis: formData.responsavel_nis || null,
          // Matrícula estendida
          etapa_ensino: formData.etapa_ensino || null,
          ano_periodo: formData.ano_periodo || null,
          // Outras características
          altas_habilidades_superdotacao: formData.altas_habilidades_superdotacao || false,
          ppl: formData.ppl || false,
          // Ficha de Matrícula: documentos, família e saúde
          ...fichaParaSalvar(formData),
        };

        // Verificar se já existe um perfil
        const existingProfile = editingAluno?.profile;
        if (existingProfile?.id) {
          // PATCH (parcial): atualiza só os campos do modal, preservando dados
          // que vêm apenas da importação (zona, bolsa família, transporte, etc.)
          await apiPatch(`/student-profiles/${existingProfile.id}/`, profileData);
        } else {
          // Tentar criar novo perfil
          try {
            await apiPost('/student-profiles/', profileData);
          } catch (profileErr) {
            // Se falhar por já existir, tentar buscar e atualizar
            console.log('Perfil pode já existir, tentando atualizar...');
          }
        }
      }

      setShowModal(false);
      fetchData();
      // Matrícula nova: oferece a ficha para imprimir e colher as assinaturas.
      if (!editingAluno && studentId && podeImprimirFicha(user)) {
        setFichaDoNovoAluno({ id_student: studentId, student_name: formData.student_name });
      }
    } catch (err) {
      console.error('Erro ao salvar aluno:', err);
      setFormErrors({ submit: err.message || 'Erro ao salvar aluno' });
    } finally {
      setSaving(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (aluno) => {
    setAlunoToDelete(aluno);
    setShowDeleteModal(true);
  };

  // Executar exclusão
  const handleDelete = async (aluno) => {
    await apiDelete(`/students/${aluno.id_student}/`);
    await fetchData();
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltroEscola('');
    setFiltroTurma('');
    setFiltroStatus('');
    setFiltroTEA('');
    setSearchTerm('');
  };

  // Obter informações da turma
  const getTurmaInfo = (idClass) => {
    const turma = turmas.find(t => t.id === idClass);
    return turma ? {
      turma: turma.class_name,
      escola: turma.school_name,
      grade: turma.grade,
      shift: turma.shift
    } : { turma: '-', escola: '-', grade: '-', shift: '-' };
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Alunos..."
        // subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/10 border border-[#E83A3C]/30'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error}</p>
          <button
            onClick={fetchData}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              }`}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Alunos
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Gerencie os alunos e visualize perfis detalhados
            {userCity && <span className="text-sideb-blue"> - {userCity.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          {canEdit && (
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Novo Aluno
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        {/* Seis colunas só cabem a partir de xl: com a barra lateral de
            260px, um notebook de 1024px deixa ~716px para a grade, e o
            filtro de AEE (min-w 160px) empurrava a página para fora. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Busca */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Buscar por nome ou matrícula..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />
          </div>

          {/* Filtro por Escola */}
          <SearchableSelect
            value={filtroEscola}
            onChange={(v) => { setFiltroEscola(v); setFiltroTurma(''); }}
            options={escolas.map(escola => ({ value: escola.id, label: escola.school }))}
            emptyOptionLabel="Todas as escolas"
            darkMode={darkMode}
            className="min-w-[180px]"
          />

          {/* Filtro por Turma */}
          <SearchableSelect
            value={filtroTurma}
            onChange={setFiltroTurma}
            options={turmasFiltradas.map(turma => ({
              value: turma.id,
              label: `${turma.class_name}${turma.school_name ? ` (${turma.school_name})` : ''}`,
            }))}
            emptyOptionLabel="Todas as turmas"
            darkMode={darkMode}
            className="min-w-[180px]"
          />

          {/* Filtro por Status */}
          <SearchableSelect
            value={filtroStatus}
            onChange={setFiltroStatus}
            options={statusOptions}
            emptyOptionLabel="Todos os status"
            darkMode={darkMode}
            className="min-w-[150px]"
          />

          {/* Filtro por TEA */}
          <SearchableSelect
            value={filtroTEA}
            onChange={setFiltroTEA}
            options={[
              { value: 'true', label: 'Com AEE (Todos)' },
              { value: 'nivel_1', label: 'Nível 1 - Leve' },
              { value: 'nivel_2', label: 'Nível 2 - Moderado' },
              { value: 'nivel_3', label: 'Nível 3 - Severo' },
              { value: 'false', label: 'Sem AEE' },
            ]}
            emptyOptionLabel="AEE: Todos"
            darkMode={darkMode}
            className="min-w-[160px]"
          />
        </div>

        {/* Limpar filtros */}
        {(filtroEscola || filtroTurma || filtroStatus || filtroTEA || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className={`text-sm ${darkMode ? 'text-[#0974BA] hover:text-[#0974BA]' : 'text-[#0974BA] hover:text-[#0974BA]'}`}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {alunos.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total de Alunos</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#13983D] to-[#0974BA]">
            {alunos.filter(a => ehAlunoMatriculado(a.status)).length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Matriculados</div>
        </div>
        <button
          type="button"
          onClick={() => setFiltroStatus(filtroStatus === STATUS_VACANTE ? '' : STATUS_VACANTE)}
          title="Alunos sem turma, à espera de remanejamento"
          className={`rounded-xl p-4 text-center transition-colors ${filtroStatus === STATUS_VACANTE
            ? 'ring-2 ring-[#2E4974]'
            : ''} ${darkMode
              ? 'bg-slate-800/50 border border-slate-700/10 hover:bg-slate-800'
              : 'bg-white/80 border border-slate-200 shadow-sm hover:bg-white'
            }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#2E4974] to-[#0974BA]">
            {alunos.filter(a => ehAlunoVacante(a.status)).length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Vacantes</div>
        </button>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#2E4974] to-[#E83A3C]">
            {alunosTEA}
          </div>
          <div className={`text-xs flex items-center justify-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <img src={TEA_BADGE} alt="TEA" className="w-4 h-4" />
            <span>Com AEE</span>
          </div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {alunosFiltrados.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Alunos Filtrados</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {turmas.length}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turmas</div>
        </div>
      </div>

      {/* Lista de Alunos */}
      {alunosFiltrados.length === 0 ? (
        <div className={`rounded-xl p-12 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-5xl mb-4">🎓</div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Nenhum aluno encontrado
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {alunos.length === 0
              ? 'Não há alunos cadastrados ainda.'
              : 'Nenhum aluno corresponde aos filtros aplicados.'}
          </p>
        </div>
      ) : (
        <div className={`rounded-xl overflow-hidden ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Aluno
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Matrícula
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Turma
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Escola
                  </th>
                  <th className={`text-left px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Status
                  </th>
                  <th className={`text-center px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    AEE
                  </th>
                  <th className={`text-right px-6 py-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                {alunosPaginados.map(aluno => {
                  const turmaInfo = getTurmaInfo(aluno.id_class);

                  return (
                    <tr
                      key={aluno.id_student}
                      className={`transition-colors cursor-pointer ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}
                      onClick={() => handleViewProfile(aluno)}
                    >
                      <td className={`px-6 py-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium ${darkMode ? 'bg-gradient-to-br from-[#0974BA]/20 to-[#2E4974]/20 text-[#0974BA]' : 'bg-gradient-to-br from-[#0974BA]/20 to-[#2E4974]/20 text-[#0974BA]'}`}>
                            {aluno.student_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="font-medium">{aluno.student_name}</span>
                        </div>
                      </td>
                      <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <code className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          {aluno.student_serial}
                        </code>
                      </td>
                      <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {ehAlunoVacante(aluno.status) ? (
                          // O vacante continua preso à última turma para poder
                          // ser reencontrado, mas ele não está mais nela.
                          <span className="flex flex-col">
                            <span>—</span>
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              era {turmaInfo.turma}
                            </span>
                          </span>
                        ) : turmaInfo.turma}
                      </td>
                      <td className={`px-6 py-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {turmaInfo.escola}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${classesStatusAluno(aluno.status)}`}>
                          {rotuloStatusAluno(aluno.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {(aluno.has_tea || aluno.tea_grau) && (
                          <div className="flex flex-col items-center gap-1" title={`Aluno AEE${aluno.tea_grau ? ` - ${teaGrauOptions.find(o => o.value === aluno.tea_grau)?.label || ''}` : ''}`}>
                            <img src={TEA_BADGE} alt="AEE" className="w-6 h-6" />
                            {aluno.tea_grau && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${aluno.tea_grau === 'nivel_1' ? 'bg-[#13983D]/20 text-[#13983D]' :
                                aluno.tea_grau === 'nivel_2' ? 'bg-[#F9B047]/20 text-[#F9B047]' :
                                  aluno.tea_grau === 'nivel_3' ? 'bg-[#E83A3C]/20 text-[#E83A3C]' :
                                    'bg-[#2E4974]/20 text-[#2E4974]'
                                }`}>
                                {aluno.tea_grau === 'nivel_1' ? 'N1' :
                                  aluno.tea_grau === 'nivel_2' ? 'N2' :
                                    aluno.tea_grau === 'nivel_3' ? 'N3' : 'AEE'}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewProfile(aluno)}
                            className={`p-2 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-[#0974BA]/20 text-slate-400 hover:text-[#0974BA]'
                              : 'hover:bg-[#0974BA]/10 text-slate-500 hover:text-[#0974BA]'
                              }`}
                            title="Ver Perfil"
                          >
                            👤
                          </button>
                          <button
                            onClick={() => handleDownloadDeclaracao(aluno)}
                            disabled={downloadingDeclaracao === aluno.id_student}
                            className={`p-2 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-emerald-500/20 text-slate-400 hover:text-emerald-400 disabled:opacity-50'
                              : 'hover:bg-emerald-500/10 text-slate-500 hover:text-emerald-600 disabled:opacity-50'
                              }`}
                            title="Gerar Declaração de Frequência (PDF)"
                          >
                            {downloadingDeclaracao === aluno.id_student ? '⏳' : '📄'}
                          </button>
                          {isDiretor && (
                            <button
                              onClick={() => abrirSituacao(aluno)}
                              className={`p-2 rounded-lg transition-colors ${darkMode
                                ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                                }`}
                              title="Mudar situação (matriculado, transferido...)"
                            >
                              🔄
                            </button>
                          )}
                          {canEdit && !isDiretor && (
                            <>
                              <button
                                onClick={() => handleEdit(aluno)}
                                className={`p-2 rounded-lg transition-colors ${darkMode
                                  ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                                  : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                                  }`}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteClick(aluno)}
                                className={`p-2 rounded-lg transition-colors ${darkMode
                                  ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                                  : 'hover:bg-[#E83A3C]/10 text-slate-500 hover:text-[#E83A3C]'
                                  }`}
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-4">
            <Pagination
              paginaAtual={pagina}
              totalItens={alunosFiltrados.length}
              itensPorPagina={porPagina}
              onPageChange={setPagina}
              onPageSizeChange={setPorPagina}
              darkMode={darkMode}
              rotuloItens="alunos"
            />
          </div>
        </div>
      )}

      {/* Modal de Criação/Edição com Abas */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className={`w-full max-w-3xl rounded-2xl ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            } my-8`}>
            {/* Header do Modal */}
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {editingAluno ? 'Editar Aluno' : 'Novo Aluno'}
              </h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Preencha os dados do aluno. Campos com * são obrigatórios.
              </p>
            </div>

            {/* Abas */}
            <div className={`flex gap-1 px-6 pt-4 overflow-x-auto ${darkMode ? 'border-b border-slate-700' : 'border-b border-slate-200'}`}>
              {[
                { id: 'basico', label: 'Básico', icon: '📋' },
                { id: 'pessoal', label: 'Pessoal', icon: '👤' },
                { id: 'endereco', label: 'Endereço', icon: '📍' },
                { id: 'responsavel', label: 'Família', icon: '👨‍👩‍👧' },
                { id: 'saude', label: 'Saúde', icon: '🩺' },
                { id: 'necessidades', label: 'Necessidades', icon: '♿' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setModalTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors rounded-t-lg ${modalTab === tab.id
                    ? darkMode
                      ? 'bg-slate-700 text-[#0974BA] border-b-2 border-[#0974BA]'
                      : 'bg-[#0974BA]/10 text-[#0974BA] border-b-2 border-[#0974BA]'
                    : darkMode
                      ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Conteúdo das Abas */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Aba Básico */}
              {modalTab === 'basico' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nome do Aluno */}
                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Nome do Aluno *
                      </label>
                      <input
                        type="text"
                        value={formData.student_name}
                        onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.student_name ? 'border-[#E83A3C]' : ''}`}
                        placeholder="Nome completo do aluno"
                      />
                      {formErrors.student_name && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.student_name}</p>}
                    </div>

                    {/* Matrícula */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Matrícula *
                      </label>
                      <input
                        type="text"
                        value={formData.student_serial}
                        onChange={(e) => setFormData({ ...formData, student_serial: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.student_serial ? 'border-[#E83A3C]' : ''}`}
                        placeholder="Número de matrícula"
                      />
                      {formErrors.student_serial && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.student_serial}</p>}
                    </div>

                    {/* Turma */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Turma *
                      </label>
                      <SearchableSelect
                        value={formData.id_class}
                        onChange={(v) => setFormData({ ...formData, id_class: v })}
                        options={turmas.map(turma => ({
                          value: turma.id,
                          label: `${turma.class_name}${turma.school_name ? ` (${turma.school_name})` : ''}`,
                        }))}
                        placeholder="Selecione uma turma"
                        required
                        darkMode={darkMode}
                        className={`w-full ${formErrors.id_class ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                      />
                      {formErrors.id_class && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.id_class}</p>}
                    </div>

                    {/* Data de Matrícula */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Data de Matrícula
                      </label>
                      <input
                        type="date"
                        value={formData.enrollment_date}
                        onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Status
                      </label>
                      <SearchableSelect
                        value={formData.status}
                        onChange={(v) => setFormData({ ...formData, status: v })}
                        options={statusOptions}
                        placeholder="Selecione"
                        darkMode={darkMode}
                        className="w-full"
                      />
                    </div>

                    {/* Etapa de Ensino */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Etapa de Ensino
                      </label>
                      <input
                        type="text"
                        value={formData.etapa_ensino}
                        onChange={(e) => setFormData({ ...formData, etapa_ensino: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Ex: Ensino Fundamental"
                      />
                    </div>

                    {/* Ano/Período */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Ano/Período
                      </label>
                      <input
                        type="text"
                        value={formData.ano_periodo}
                        onChange={(e) => setFormData({ ...formData, ano_periodo: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Ex: 5º Ano"
                      />
                    </div>

                    {/* Turno */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Turno
                      </label>
                      <SearchableSelect
                        value={formData.turno || ''}
                        onChange={(v) => setFormData({ ...formData, turno: v })}
                        options={[
                          { value: 'manha', label: 'Manhã' },
                          { value: 'tarde', label: 'Tarde' },
                          { value: 'noite', label: 'Noite' },
                          { value: 'integral', label: 'Integral' },
                        ]}
                        placeholder="Selecione"
                        darkMode={darkMode}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Aba Pessoal */}
              {modalTab === 'pessoal' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CPF */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        CPF
                      </label>
                      <input
                        type="text"
                        value={formData.cpf}
                        onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="000.000.000-00"
                      />
                    </div>

                    {/* NIS */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        NIS
                      </label>
                      <input
                        type="text"
                        value={formData.nis}
                        onChange={(e) => setFormData({ ...formData, nis: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Número do NIS"
                      />
                    </div>

                    {/* Nome Social */}
                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Nome Social
                      </label>
                      <input
                        type="text"
                        value={formData.nome_social}
                        onChange={(e) => setFormData({ ...formData, nome_social: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Nome social (se aplicável)"
                      />
                    </div>

                    {/* Data de Nascimento */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Data de Nascimento
                      </label>
                      <input
                        type="date"
                        value={formData.data_nascimento}
                        onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                      />
                    </div>

                    {/* Gênero */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Gênero
                      </label>
                      <SearchableSelect
                        value={formData.genero}
                        onChange={(v) => setFormData({ ...formData, genero: v })}
                        options={[
                          { value: 'masculino', label: 'Masculino' },
                          { value: 'feminino', label: 'Feminino' },
                          { value: 'outro', label: 'Outro' },
                        ]}
                        placeholder="Selecione"
                        darkMode={darkMode}
                        className="w-full"
                      />
                    </div>

                    {/* Cor / Raça / Etnia */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Cor / Raça / Etnia
                      </label>
                      <SearchableSelect
                        value={formData.raca_cor}
                        onChange={(v) => setFormData({ ...formData, raca_cor: v })}
                        options={[
                          { value: 'branca', label: 'Branca' },
                          { value: 'preta', label: 'Preta' },
                          { value: 'parda', label: 'Parda' },
                          { value: 'amarela', label: 'Amarela' },
                          { value: 'indigena', label: 'Indígena' },
                          { value: 'quilombola', label: 'Quilombola' },
                          { value: 'nao_declarada', label: 'Não declarada' },
                        ]}
                        placeholder="Selecione"
                        darkMode={darkMode}
                        className="w-full"
                      />
                    </div>

                    {/* E-mail */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        E-mail
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="email@exemplo.com"
                      />
                    </div>

                    {/* RG */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        RG
                      </label>
                      <input
                        type="text"
                        value={formData.rg}
                        onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Número do RG"
                      />
                    </div>

                    {/* Órgão Emissor RG */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Órgão Emissor
                      </label>
                      <input
                        type="text"
                        value={formData.orgao_emissor_rg}
                        onChange={(e) => setFormData({ ...formData, orgao_emissor_rg: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="SSP, DETRAN, etc"
                      />
                    </div>

                    {/* Certidão, SUS e naturalidade (Ficha de Matrícula) */}
                    <CamposDocumentosDaFicha formData={formData} setFormData={setFormData} darkMode={darkMode} />
                  </div>
                </div>
              )}

              {/* Aba Endereço */}
              {modalTab === 'endereco' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Logradouro */}
                    <div className="md:col-span-2">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Logradouro
                      </label>
                      <input
                        type="text"
                        value={formData.logradouro}
                        onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Rua, Avenida, etc"
                      />
                    </div>

                    {/* Número */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Número
                      </label>
                      <input
                        type="text"
                        value={formData.numero_endereco}
                        onChange={(e) => setFormData({ ...formData, numero_endereco: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="123"
                      />
                    </div>

                    {/* Bairro */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Bairro
                      </label>
                      <input
                        type="text"
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Centro"
                      />
                    </div>

                    {/* CEP */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        CEP
                      </label>
                      <input
                        type="text"
                        value={formData.cep}
                        onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="00000-000"
                      />
                    </div>

                    {/* Município */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Código do Município
                      </label>
                      <input
                        type="text"
                        value={formData.co_municipio}
                        onChange={(e) => setFormData({ ...formData, co_municipio: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                          : 'bg-slate-50 border-slate-200 text-slate-700'
                          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                        placeholder="Código IBGE"
                      />
                    </div>

                    {/* Cidade (o código IBGE não diz o nome na ficha) */}
                    <CampoCidadeDaFicha formData={formData} setFormData={setFormData} darkMode={darkMode} />

                    {/* UF */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        UF
                      </label>
                      <SearchableSelect
                        value={formData.co_uf}
                        onChange={(v) => setFormData({ ...formData, co_uf: v })}
                        options={['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']}
                        placeholder="Selecione"
                        darkMode={darkMode}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Aba Família: mãe, pai, responsável e outro contato */}
              {modalTab === 'responsavel' && (
                <AbaFamilia formData={formData} setFormData={setFormData} darkMode={darkMode} />
              )}

              {/* Aba Saúde (seção IV da Ficha de Matrícula) */}
              {modalTab === 'saude' && (
                <AbaSaude formData={formData} setFormData={setFormData} darkMode={darkMode} />
              )}

              {/* Aba Necessidades Especiais */}
              {modalTab === 'necessidades' && (
                <div className="space-y-4">
                  {/* AEE */}
                  <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#2E4974]/10 border border-[#2E4974]/20' : 'bg-[#2E4974]/10 border border-[#2E4974]/30'}`}>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      <div className="flex items-center gap-2">
                        <img src={TEA_BADGE} alt="Nível de suporte" className="w-5 h-5" />
                        <span>Nível de suporte</span>
                      </div>
                    </label>
                    <SearchableSelect
                      value={formData.tea_grau}
                      onChange={(v) => setFormData({ ...formData, tea_grau: v, transtorno_espectro_autista: v ? true : false })}
                      options={teaGrauOptions}
                      placeholder="Selecione"
                      darkMode={darkMode}
                      className="w-full"
                    />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Selecione o grau conforme diagnóstico médico (DSM-5)
                    </p>
                  </div>

                  {/* Deficiências */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Deficiências
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { field: 'deficiencia_baixa_visao', label: 'Baixa Visão' },
                        { field: 'deficiencia_cegueira', label: 'Cegueira' },
                        { field: 'deficiencia_auditiva', label: 'Deficiência Auditiva' },
                        { field: 'deficiencia_fisica', label: 'Deficiência Física' },
                        { field: 'deficiencia_intelectual', label: 'Deficiência Intelectual' },
                        { field: 'deficiencia_surdez', label: 'Surdez' },
                        { field: 'deficiencia_surdocegueira', label: 'Surdocegueira' },
                        { field: 'deficiencia_multipla', label: 'Deficiência Múltipla' },
                      ].map(item => (
                        <label
                          key={item.field}
                          className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${formData[item.field]
                            ? darkMode ? 'bg-[#2E4974]/20 border border-[#2E4974]/30' : 'bg-[#2E4974]/10 border border-[#2E4974]/30'
                            : darkMode ? 'bg-slate-700/30 border border-slate-600 hover:bg-slate-700/50' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData[item.field] || false}
                            onChange={(e) => setFormData({ ...formData, [item.field]: e.target.checked })}
                            className="w-4 h-4 rounded border-slate-300 text-[#2E4974] focus:ring-[#2E4974]"
                          />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{item.label}</span>
                        </label>
                      ))}
                      {/* Outras deficiências */}
                      <label
                        className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${formData.deficiencia_outras
                          ? darkMode ? 'bg-[#2E4974]/20 border border-[#2E4974]/30' : 'bg-[#2E4974]/10 border border-[#2E4974]/30'
                          : darkMode ? 'bg-slate-700/30 border border-slate-600 hover:bg-slate-700/50' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.deficiencia_outras || false}
                          onChange={(e) => setFormData({ ...formData, deficiencia_outras: e.target.checked, ...(!e.target.checked && { deficiencia_outras_descricao: '' }) })}
                          className="w-4 h-4 rounded border-slate-300 text-[#2E4974] focus:ring-[#2E4974]"
                        />
                        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Outras</span>
                      </label>
                    </div>
                    {formData.deficiencia_outras && (
                      <div className="mt-3">
                        <input
                          type="text"
                          value={formData.deficiencia_outras_descricao}
                          onChange={(e) => setFormData({ ...formData, deficiencia_outras_descricao: e.target.value })}
                          placeholder="Descreva a(s) outra(s) deficiência(s)"
                          className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                            ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                            : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                            } border focus:outline-none focus:ring-2 focus:ring-[#2E4974]/50`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Outras características */}
                  <div>
                    <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Outras Características
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <label
                        className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${formData.altas_habilidades_superdotacao
                          ? darkMode ? 'bg-[#0974BA]/20 border border-[#0974BA]/30' : 'bg-[#0974BA]/10 border border-[#0974BA]/30'
                          : darkMode ? 'bg-slate-700/30 border border-slate-600 hover:bg-slate-700/50' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.altas_habilidades_superdotacao || false}
                          onChange={(e) => setFormData({ ...formData, altas_habilidades_superdotacao: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                        />
                        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Altas Habilidades/Superdotação</span>
                      </label>

                      <label
                        className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${formData.ppl
                          ? darkMode ? 'bg-[#F9B047]/20 border border-[#F9B047]/30' : 'bg-[#F9B047]/10 border border-[#F9B047]/30'
                          : darkMode ? 'bg-slate-700/30 border border-slate-600 hover:bg-slate-700/50' : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.ppl || false}
                          onChange={(e) => setFormData({ ...formData, ppl: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300 text-[#F9B047] focus:ring-[#F9B047]"
                        />
                        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Privação de Liberdade (PPL)</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Erro de submit */}
              {formErrors.submit && (
                <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/10 border border-[#E83A3C]/30'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{formErrors.submit}</p>
                </div>
              )}
            </div>

            {/* Footer do Modal */}
            <div className={`flex justify-between gap-3 p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                * Campos obrigatórios
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                    }`}
                >
                  {saving ? 'Salvando...' : editingAluno ? 'Salvar Alterações' : 'Criar Aluno'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matrícula feita: imprimir a ficha */}
      {fichaDoNovoAluno && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
            <div className="w-12 h-12 rounded-full bg-[#13983D]/15 text-[#13983D] flex items-center justify-center text-2xl mb-4">✓</div>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
              Matrícula feita
            </h3>
            <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <strong>{fichaDoNovoAluno.student_name}</strong> foi matriculado(a). Imprima a Ficha de
              Matrícula para o responsável conferir os dados e assinar.
            </p>
            {erroFicha && <p className="mt-3 text-sm text-[#E83A3C]">{erroFicha}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => { setFichaDoNovoAluno(null); setErroFicha(null); }}
                className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Agora não
              </button>
              <button
                type="button"
                disabled={gerandoFicha}
                onClick={async () => {
                  setGerandoFicha(true);
                  setErroFicha(null);
                  try {
                    await baixarDocumentoAluno(fichaDoNovoAluno, 'ficha_matricula');
                    setFichaDoNovoAluno(null);
                  } catch (err) {
                    setErroFicha(err.message);
                  } finally {
                    setGerandoFicha(false);
                  }
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0974BA] hover:bg-[#0974BA]/85 text-white disabled:opacity-60"
              >
                {gerandoFicha ? 'Gerando...' : '📄 Imprimir Ficha de Matrícula'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mudar situação — o que o Diretor(a) faz no aluno que já existe */}
      {alunoSituacao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl p-6 ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white shadow-xl'}`}>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
              Mudar situação
            </h3>
            <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <strong>{alunoSituacao.student_name}</strong> — hoje:{' '}
              {rotuloStatusAluno(alunoSituacao.status)}
            </p>
            <label className={`block text-sm font-medium mt-4 mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Nova situação
            </label>
            <SearchableSelect
              value={novaSituacao}
              onChange={setNovaSituacao}
              options={statusOptions}
              placeholder="Selecione"
              darkMode={darkMode}
              className="w-full"
            />
            <p className={`mt-3 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Transferido, concluído, desistente ou vacante sai da chamada da
              turma; o que já foi lançado (frequência e notas) continua no
              histórico. Para corrigir nome, matrícula, turma ou dados da
              ficha, procure a Secretaria de Educação.
            </p>
            {erroSituacao && <p className="mt-3 text-sm text-[#E83A3C]">{erroSituacao}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setAlunoSituacao(null)}
                className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={salvandoSituacao || !novaSituacao || novaSituacao === normalizarStatusAluno(alunoSituacao.status)}
                onClick={salvarSituacao}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0974BA] hover:bg-[#0974BA]/85 text-white disabled:opacity-60"
              >
                {salvandoSituacao ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAlunoToDelete(null);
        }}
        onConfirm={handleDelete}
        item={alunoToDelete}
        itemName={alunoToDelete?.student_name}
        itemType="Aluno"
        warningMessage="Todos os dados do aluno serão removidos. Digite sua senha para confirmar."
      />
    </>
  );
}

export default Alunos;
